import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AlertTriangle, LogOut, Copy } from 'lucide-react';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { useStaffRoles } from '@/hooks/useStaffRoles';
import { useToast } from '@/hooks/use-toast';

export const ImpersonationBanner: React.FC = () => {
  const location = useLocation();
  const { hasActiveSession, impersonationState, endImpersonation, getActiveMagicLink } = useImpersonation();
  const { isSupport, isSuperAdmin } = useStaffRoles();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  // Since Queue Assist is removed, never show the banner
  const shouldShowBanner = false;

  if (!shouldShowBanner) {
    return null;
  }

  const handleCopyMagicLink = async () => {
    setIsLoading(true);
    try {
      const result = await getActiveMagicLink();
      
      if (result.success && result.magicLink) {
        await navigator.clipboard.writeText(result.magicLink);
        toast({
          title: "Link Copied",
          description: "Magic link has been copied to clipboard. Open in incognito window.",
        });
      } else {
        toast({
          title: "Error",
          description: result.error || "Failed to get magic link",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy link to clipboard",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEndImpersonation = async () => {
    setIsLoading(true);
    try {
      const result = await endImpersonation();
      
      if (result.success) {
        toast({
          title: "Session Closed",
          description: "Impersonation session has been closed",
        });
      } else {
        toast({
          title: "Error",
          description: result.error || "Failed to close session",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to close session",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-amber-500 text-amber-900 px-4 py-2 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-5 w-5" />
        <span className="font-medium">
          Active Impersonation Session: {impersonationState.targetUserInfo?.displayName || impersonationState.targetUserInfo?.email}
        </span>
        <span className="text-sm opacity-80">
          (Staff Mode)
        </span>
      </div>
      
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleCopyMagicLink}
          disabled={isLoading}
          className="bg-amber-100 text-amber-900 border-amber-700 hover:bg-amber-200"
        >
          <Copy className="h-4 w-4 mr-2" />
          Copy Magic Link
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleEndImpersonation}
          disabled={isLoading}
          className="bg-amber-100 text-amber-900 border-amber-700 hover:bg-amber-200"
        >
          <LogOut className="h-4 w-4 mr-2" />
          Close Session
        </Button>
      </div>
    </div>
  );
};